<script lang="ts">
  import Icon from './Icon.svelte';

  /**
   * Floating Action Button (style Material) avec 2 actions de création :
   *  - Importer une photo
   *  - Grille vierge
   *
   * Comportement :
   *  - État fermé : bouton circulaire 56px en bas-droite du canvas-wrap
   *  - Tap → backdrop semi-transparent + chips empilés au-dessus + rotation
   *    45° de l'icône `+` (devient un `×` visuel)
   *  - Tap sur backdrop, sur action, ou re-tap FAB → fermeture
   *
   * Source de vérité unique pour la création de patron sur mobile.
   * Pas de duplication dans le MobilePanel (qui ne contient plus que les
   * actions sur le patron courant).
   */

  type Props = {
    /** Désactive le bouton (cap atteint). Tap = ouvre une alerte par le caller. */
    disabled?: boolean;
    /** Tooltip / aria-label en cas de disabled (ex. « limite atteinte »). */
    disabledTitle?: string;
    onImport: () => void;
    onBlank: () => void;
  };

  let {
    disabled = false,
    disabledTitle = '',
    onImport,
    onBlank,
  }: Props = $props();

  let open = $state(false);

  function toggle(): void {
    if (disabled) return;
    open = !open;
  }
  function close(): void {
    open = false;
  }
  function pick(action: () => void): void {
    open = false;
    action();
  }
</script>

<div class="fab-menu" class:fab-menu--open={open}>
  {#if open}
    <button
      type="button"
      class="fab-menu__backdrop"
      aria-label="Fermer le menu"
      onclick={close}
    ></button>
    <div class="fab-menu__items" role="menu">
      <button
        type="button"
        class="fab-menu__item"
        role="menuitem"
        onclick={() => pick(onImport)}
      >
        <Icon name="image" size={20} />
        <span>Importer une photo</span>
      </button>
      <button
        type="button"
        class="fab-menu__item"
        role="menuitem"
        onclick={() => pick(onBlank)}
      >
        <Icon name="grid" size={20} />
        <span>Grille vierge</span>
      </button>
    </div>
  {/if}

  <button
    type="button"
    class="fab-menu__main"
    aria-label={open ? 'Fermer le menu de création' : 'Créer un patron'}
    aria-expanded={open}
    {disabled}
    title={disabled ? disabledTitle : undefined}
    onclick={toggle}
  >
    <span class="fab-menu__icon" class:fab-menu__icon--rotated={open}>
      <Icon name="plus" size={26} />
    </span>
  </button>
</div>

<style>
  .fab-menu {
    /* Ancré dans canvas-wrap qui est position: relative. Au-dessus du dock
       mobile (qui fait ~120px de haut + 8px d'offset). */
    position: absolute;
    bottom: calc(var(--space-2) + 140px);
    right: var(--space-3);
    z-index: 30;
  }
  .fab-menu__backdrop {
    position: fixed;
    inset: 0;
    background: rgb(20 32 44 / 0.35);
    border: 0;
    padding: 0;
    z-index: 28;
    cursor: pointer;
  }
  .fab-menu__items {
    position: absolute;
    bottom: calc(100% + var(--space-3));
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-2);
    z-index: 31;
  }
  .fab-menu__item {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: var(--space-3) 18px;
    background: var(--color-background);
    border: 0;
    border-radius: 999px;
    box-shadow: var(--shadow-md);
    color: var(--color-text);
    font: inherit;
    font-size: var(--text-small);
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  @media (hover: hover) {
    .fab-menu__item:hover {
      background: var(--color-surface);
    }
  }
  .fab-menu__main {
    position: relative;
    z-index: 32;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--color-accent);
    color: var(--color-background);
    border: 0;
    box-shadow: 0 6px 14px rgb(0 0 0 / 0.22);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.15s ease;
  }
  .fab-menu__main:hover:not(:disabled) {
    background: var(--color-accent-hover, var(--color-accent));
  }
  .fab-menu__main:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .fab-menu__icon {
    display: inline-flex;
    transition: transform 0.18s ease-out;
  }
  .fab-menu__icon--rotated {
    transform: rotate(45deg);
  }
</style>
